import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { DEFAULT_PIS, PIS_PRIVACY_DOC_URL, PIS_TC_DOC_URL } from 'src/config'
import {
  useGetPisIsAlive,
  useGetPisProducts,
  usePostPisPayment,
} from 'src/networking/terminals/pis/pis.gates'
import {
  atomFormikPolicy,
  atomPaymentData,
  atomPisPaymentId,
  atomPostPolicyResponse,
  selectorInsured,
} from 'src/storage'
import { InputSelectOption, PolicyConfigurationProps } from 'src/types'
import { AxeptaWindow } from 'src/types/window'

export const usePolicyConfigurationStep4 = (props: PolicyConfigurationProps) => {
  const { t } = useTranslation()
  const { formik } = props

  const isPisDefault = DEFAULT_PIS
  const [paymentMethod, setPaymentMethod] = useState<string>('')
  const [isPisAlive, setIsPisAlive] = useState<boolean>()
  const [hasBankProducts, setHasBankProducts] = useState<boolean>(false)
  const [providerId, setProviderId] = useState<string>('')
  const [bankProducts, setBankProducts] = useState<InputSelectOption[]>([])
  const [paymentTypes, setPaymentTypes] = useState<InputSelectOption[]>([])

  const [formikPolicy, saveFormikPolicy] = useRecoilState(atomFormikPolicy)
  const insured = useRecoilValue(selectorInsured)
  const postPolicyResponse = useRecoilValue(atomPostPolicyResponse)
  const paymentData = useRecoilValue(atomPaymentData)
  const setPisPaymentId = useSetRecoilState(atomPisPaymentId)
  const pisTermsConditionsUrl = PIS_TC_DOC_URL
  const pisPrivacyUrl = PIS_PRIVACY_DOC_URL

  const { crossGate: getPisProducts } = useGetPisProducts()
  const { crossGate: getPisIsAlive } = useGetPisIsAlive()
  const { crossGate: postPisPayment } = usePostPisPayment()

  const paymentMethodsOptions: InputSelectOption[] = [
    {
      id: 'cardMethod',
      value: t('policy_configuration.step_4.payment_options_data.card_label'),
    },
    {
      id: 'bankTransferMethod',
      value: t('policy_configuration.step_4.payment_options_data.bank_transfer_label'),
    },
  ]

  useEffect(() => {
    resetPisFormData()

    const element = document.getElementById('payment-container')
    if (element) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    if (!isPisDefault) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement && node.classList.contains('axepta-sdk-flex-row')) {
              // initially hide the back button for MyBank as it is the default value
              const backBtnElement = document.getElementById('axepta-sdk-back-button')
              if (backBtnElement) {
                backBtnElement.style.display = 'none'
              }
            }
          })
        })
      })

      observer.observe(document.body, { childList: true, subtree: true })

      return () => observer.disconnect()
    }
    return () => {}
  }, [])

  useEffect(() => {
    if (paymentData?.paymentID) {
      if (isPisDefault) {
        checkPis()
        formik.setFieldValue('iban', '')
        resetPisFields()
      } else {
        setIsPisAlive(false)
        formik.setFieldValue('paymentMethod', 'cardMethod')
        formik.setFieldValue('iban', '')
        resetPisFields()
      }
    } else {
      formik.setFieldValue('iban', '')
      resetPisFields()
    }
  }, [paymentData])

  useEffect(() => {
    setPaymentMethod(formik.values.paymentMethod)
  }, [formik.values.paymentMethod])

  useEffect(() => {
    if (paymentData?.paymentID) {
      if (
        paymentMethod === 'cardMethod' ||
        (paymentMethod === 'bankTransferMethod' && isPisAlive === false)
      ) {
        setCardMyBankPayment()
        formik.setFieldValue('iban', '')
        resetPisFields()
      }
    }
  }, [isPisAlive, paymentMethod])

  useEffect(() => {
    if (isPisAlive) {
      resetPisFields()
      onCheckIban()
    }
  }, [isPisAlive, formik.errors.iban])

  const resetPisFields = () => {
    setBankProducts([])
    setPaymentTypes([])
    formik.setFieldValue('bankProduct', '')
    formik.setFieldValue('paymentType', '')
    formik.setFieldValue('consentPayment', false)
  }

  const setCardMyBankPayment = () => {
    // console.warn('......................INIT AXEPTA')
    if (paymentData?.paymentID) {
      const axeptaWindow: AxeptaWindow = window
      axeptaWindow?.callAxeptaClient?.()?.preparePayment?.(paymentData?.paymentID)

      const paymentContainer = document.getElementById('payment-container') as Node
      if (paymentContainer) {
        const checkForBackBtn = () => {
          const backBtnElement = document.getElementById('axepta-sdk-back-button')
          if (backBtnElement) {
            if (paymentMethod === 'bankTransferMethod') {
              backBtnElement.style.display = 'none'
            } else {
              backBtnElement.style.display = 'flex'
            }
            backBtnElement.addEventListener('click', handleClick)
            backBtnObserver.disconnect()
          }
        }

        const handleClick = () => {
          setPaymentObservers()
        }

        const backBtnObserver = new MutationObserver(() => {
          checkForBackBtn()
        })

        backBtnObserver.observe(paymentContainer, {
          childList: true,
          subtree: true,
        })

        setPaymentObservers()
      }
    }
  }

  const setPaymentObservers = () => {
    const paymentContainer = document.getElementById('payment-container') as Node
    // console.error('...............PAYMENT OBSERVERS', paymentMethod, paymentContainer)
    if (paymentContainer) {
      // check for card, satispay and mybank buttons
      const checkForCardBtn = () => {
        const cardMethodOptionElement = document.getElementById(
          'axepta-sdk-selector-button-CREDITCARD'
        )
        // console.log('...............checkForCardBtn', cardMethodOptionElement)
        if (cardMethodOptionElement) {
          cardObserver.disconnect()
        }
      }
      const checkForSatispayBtn = () => {
        const satispayMethodOptionElement = document.getElementById(
          'axepta-sdk-selector-button-SATISPAY'
        )
        // console.log('...............checkForSatispayBtn', satispayMethodOptionElement)
        if (satispayMethodOptionElement) {
          if (paymentMethod === 'cardMethod') {
            satispayMethodOptionElement.style.display = 'flex'
          } else {
            satispayMethodOptionElement.style.display = 'none'
          }

          satispayObserver.disconnect()
        }
      }
      const checkForMyBankBtn = () => {
        const myBankMethodOptionElement = document.getElementById(
          'axepta-sdk-selector-button-MYBANK'
        )
        // console.log('...............checkForMyBankBtn', myBankMethodOptionElement)
        if (myBankMethodOptionElement) {
          // console.log('................MY BANK METHOD OPTION ELEMENT', myBankMethodOptionElement)
          if (paymentMethod === 'cardMethod') {
            myBankMethodOptionElement.style.display = 'none'
          } else {
            myBankMethodOptionElement.style.display = 'flex'
          }

          // TODO: move it to MyBank section
          if (paymentMethod === 'bankTransferMethod') {
            // show card form fields directly
            myBankMethodOptionElement.addEventListener('click', () => {
              const bankRadioBtn = document.getElementById('paymentMethod') as HTMLInputElement
              if (bankRadioBtn) bankRadioBtn.value = 'bankTranferMethod'
            })
            myBankMethodOptionElement?.click()
          }

          myBankObserver.disconnect()
        }
      }

      // create observers for each button
      const cardObserver = new MutationObserver(() => {
        checkForCardBtn()
      })
      const satispayObserver = new MutationObserver(() => {
        checkForSatispayBtn()
      })
      const myBankObserver = new MutationObserver(() => {
        checkForMyBankBtn()
      })

      // start watching the payment container in order to detect each button's creation
      cardObserver.observe(paymentContainer, {
        childList: true,
        subtree: true,
      })
      satispayObserver.observe(paymentContainer, {
        childList: true,
        subtree: true,
      })
      myBankObserver.observe(paymentContainer, {
        childList: true,
        subtree: true,
      })

      return () => {
        cardObserver.disconnect()
        satispayObserver.disconnect()
        myBankObserver.disconnect()
      }
    }
    return () => {}
  }

  const resetPisFormData = () => {
    setBankProducts([])
    setPaymentTypes([])

    formik.setFieldValue('iban', '')
    formik.setFieldValue('bankProduct', '')
    formik.setFieldValue('paymentType', '')
    formik.setFieldValue('consentPayment', false)

    saveFormikPolicy({
      ...formikPolicy,
      iban: '',
      bankProduct: '',
      paymentType: '',
      consentPayment: false,
    })
  }

  const onCheckIban = () => {
    setHasBankProducts(false)
    // if the user typed a correct iban and stop focusing the iban field
    if (formik.values.iban && !formik.errors.iban) {
      // get the products and the payment types
      const iban = formik.values.iban.replace(/\s+/g, '').toUpperCase()
      getBankProducts(iban)
    }
  }

  const checkPis = () => {
    getPisIsAlive(
      {},
      {
        onSuccess: (response) => {
          if (response && response.status === 'OK') {
            setPaymentMethod('bankTransferMethod')
            formik.setFieldValue('paymentMethod', 'bankTransferMethod')
            setIsPisAlive(true)
          } else {
            setPaymentMethod('cardMethod')
            formik.setFieldValue('paymentMethod', 'cardMethod')
            setIsPisAlive(false)
          }
        },
        onFailure: () => {
          setPaymentMethod('cardMethod')
          formik.setFieldValue('paymentMethod', 'cardMethod')
          setIsPisAlive(false)
        },
      }
    )
  }

  const getBankProducts = (iban: string) => {
    getPisProducts(
      {
        uriParams: { iban },
      },
      {
        onSuccess: (response) => {
          response.map((data) => {
            setHasBankProducts(true)
            setProviderId(data.providerId)
            setBankProducts(
              data.providerProducts.map((providerProduct) => {
                return {
                  value: providerProduct.description,
                  id: providerProduct.productCode,
                }
              })
            )
            setPaymentTypes(
              data.paymentProducts.map((paymentProduct) => {
                return {
                  value: paymentProduct.description,
                  id: paymentProduct.productCode,
                }
              })
            )
          })
        },
      }
    )
  }

  const onPisPaymentSubmit = () => {
    const values = formik.values
    if (insured) {
      const postPisPaymentBody = {
        proclie: insured.idAssicurato,
        email: insured.email,
        iban: values.iban,
        providerId: providerId,
        providerProductCode: values.bankProduct,
        paymentProductCode: values.paymentType,
        consensi: {
          informativa_precontrattuale: values.consentInformativeSet,
          tramissione_digitale: values.consentDigitalTransmission,
          privacy: values.consentPrivacy,
          marketing: values.consentMarketing,
          profilazione: values.consentProfiling,
          cessione_dati_to_itas: values.consentPersonalData,
        },
      }

      postPisPayment(
        {
          uriParams: {
            idPolizza: postPolicyResponse.polizza.code,
          },
          body: postPisPaymentBody,
        },
        {
          onSuccess: ({ paymentUrl, paymentID }) => {
            setPisPaymentId(paymentID)
            resetPisFormData()
            window.open(paymentUrl, '_self')
          },
        }
      )
    }
  }

  return {
    t,
    paymentMethodsOptions,
    bankProducts,
    paymentTypes,
    isPisAlive,
    insured,
    paymentId: paymentData?.paymentID,
    hasBankProducts,
    pisTermsConditionsUrl,
    pisPrivacyUrl,
    onPisPaymentSubmit,
  }
}
